import { useEffect, useState } from 'react'
import { CollapsibleSection } from './CollapsibleSection'
import { parseFirmwareVersion, parseHardwareVersion, roundToDecimal } from '../common/utils'
import { api } from '../services'
import { DeviceInfo } from '../common/enums'

interface Props {
  device: DeviceInfo
  showBatteryVoltage?: boolean
  open?: boolean
}

export function DeviceDetails({ device, showBatteryVoltage = true, open = false }: Props) {
  const [data, setData] = useState<string[][]>([])
  const [lastBatteryReading, setLastBatteryReading] = useState(0)
  const [title, setTitle] = useState('RIC Name: ')

  useEffect(() => {
    if (!device?.sigfoxId) return

    sync()
    async function sync() {
      const response = await api.getLatestBatteryReading(device.sigfoxId)
      // Response shape is { data: { battery: { ts: volts }, power: {...} } }.
      const battery = response?.data?.battery
      if (!battery || typeof battery !== 'object') {
        setLastBatteryReading(0)
        return
      }
      const lastTimestamp = Object.keys(battery).sort((a, b) => Number(b) - Number(a))[0]
      setLastBatteryReading(lastTimestamp ? Number(battery[lastTimestamp]) : 0)
    }
  }, [device])

  useEffect(() => {
    if (device) {
      setTitle('RIC Name: ' + device.deviceName)

      const data = [
        [
          `Serial Number: ${device.serialNumber}`,
          `WISOL ID: ${device.wisolId}`,
          `Hardware: ${parseHardwareVersion(device.hardwareNumber)}`,
          `Firmware: ${parseFirmwareVersion(device.firmwareVersion)}`,
        ],
      ]

      if (showBatteryVoltage) {
        data.push([`Battery Reading: ${roundToDecimal(lastBatteryReading, 2)} V`])
      }

      setData(data)
    }
  }, [device, lastBatteryReading, showBatteryVoltage])

  return (
    <CollapsibleSection title={title} open={open}>
      <table className={tailwind.table}>
        <tbody>
          {data.map((row, index) => (
            <tr key={index} className={index % 2 === 0 ? tailwind.even : tailwind.odd}>
              {row.map((item, index) => (
                <td key={index}>{item}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </CollapsibleSection>
  )
}

const tailwind = {
  table: 'table-fixed w-full',
  even: 'text-black text-l font-regular h-10 py-2.5 pl-4 pr-4 w-full border-b bg-[#f9f9f9]',
  odd: 'text-black text-l font-regular h-10 py-2.5 pl-4 pr-4 w-full border-b bg-[#fff]',
}
